import type { settingTypes } from '@ipfabric/utils';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type ForcePointFormValues =
  BaseVendorFormValues<settingTypes.ForcePointApiSettings>;

const resetSecretAttributes = (
  values: ForcePointFormValues,
): ForcePointFormValues => {
  return {
    ...values,
    authenticationKey: '',
  };
};

type Props = VendorDialogProps<
  ForcePointFormValues,
  settingTypes.ForcePointApiSettings,
  settingTypes.UpdateForcePointApiSettings
>;

const BareForcePointForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const formik = useFormik<ForcePointFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { required, baseUrlStartsWith } =
        msg.discoverySettings.vendorsApi.validations;
      const errors: FormikErrors<ForcePointFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = baseUrlStartsWith;
      }

      if (!editMode && !values.authenticationKey) {
        errors.authenticationKey = required;
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: ForcePointFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          authenticationKey:
            values.authenticationKey ||
            props.initialValues.authenticationKey ||
            undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<ForcePointFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikVendorApiBaseUrlField formik={formik} />
      <FormikTextField
        formik={formik}
        label={
          msg.discoverySettings.vendorsApi.forcepointForm.authenticationKey
        }
        name='authenticationKey'
        type='text'
        placeholder={editMode ? '(unchanged)' : undefined}
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const ForcePointForm = memo(BareForcePointForm);
